import { ArchiveIcon, Clock3Icon, StarIcon, Undo2Icon } from "lucide-react";
import { useState } from "react";
import { resolveSnoozePresets } from "../Sidebar.snooze";
import { useClientSettings } from "../../hooks/useSettings";
import { Button } from "../ui/button";
import { Tooltip, TooltipTrigger, TooltipPopup } from "../ui/tooltip";
import {
  Menu,
  MenuTrigger,
  MenuPopup,
  MenuItem,
  MenuShortcut,
  MenuSub,
  MenuSubTrigger,
  MenuSubPopup,
} from "../ui/menu";
import type { InboxTriage } from "./inboxTriage";

export interface TriageControlProps {
  state: InboxTriage;
  disabled: boolean;
  onChange: (patch: InboxTriage) => void;
}
export function InboxSnoozeItems({ onChange }: Pick<TriageControlProps, "onChange">) {
  const format = useClientSettings((settings) => settings.timestampFormat);
  return resolveSnoozePresets(new Date(), format).map((preset) => (
    <MenuItem
      key={preset.id}
      onClick={() => onChange({ settled: false, snoozedUntil: preset.snoozedUntil })}
    >
      <span className="flex-1">{preset.label}</span>
      <span className="ml-4 text-xs text-muted-foreground">{preset.whenLabel}</span>
    </MenuItem>
  ));
}
export function InboxTriageMenuItems({ state, disabled, onChange }: TriageControlProps) {
  return (
    <>
      <MenuItem
        disabled={disabled}
        onClick={() => onChange({ settled: !state.settled, snoozedUntil: undefined })}
      >
        {state.settled ? <Undo2Icon /> : <ArchiveIcon />}
        {state.settled ? "Move to inbox" : "Archive update"}
        <MenuShortcut>E</MenuShortcut>
      </MenuItem>
      <MenuItem disabled={disabled} onClick={() => onChange({ important: !state.important })}>
        <StarIcon />
        {state.important ? "Mark as not important" : "Mark as important"}
      </MenuItem>
      {Date.parse(state.snoozedUntil ?? "") > Date.now() ? (
        <MenuItem disabled={disabled} onClick={() => onChange({ snoozedUntil: undefined })}>
          <Clock3Icon />
          Unsnooze
        </MenuItem>
      ) : (
        <MenuSub>
          <MenuSubTrigger disabled={disabled}>
            <Clock3Icon />
            Snooze
          </MenuSubTrigger>
          <MenuSubPopup>
            <InboxSnoozeItems onChange={onChange} />
          </MenuSubPopup>
        </MenuSub>
      )}
    </>
  );
}
export function InboxTriageControls({
  state,
  disabled,
  onChange,
  compact = false,
}: TriageControlProps & { compact?: boolean }) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const snoozed = Date.parse(state.snoozedUntil ?? "") > Date.now();
  const importanceLabel = state.important ? "Mark as not important" : "Mark as important";
  const archiveLabel = state.settled ? "Move to inbox" : "Archive update";
  const iconSize = compact ? "size-2.5" : "size-3";
  return (
    <div
      className="inbox-triage-controls flex items-center gap-0.5"
      data-open={snoozeOpen || undefined}
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              size={compact ? "icon-micro" : "icon-xs"}
              variant="ghost-muted"
              disabled={disabled}
              aria-pressed={!!state.important}
              aria-label={importanceLabel}
              onClick={() => onChange({ important: !state.important })}
            />
          }
        >
          <StarIcon
            className={`${iconSize} ${state.important ? "fill-amber-500 text-amber-600" : ""}`}
          />
        </TooltipTrigger>
        <TooltipPopup>{importanceLabel}</TooltipPopup>
      </Tooltip>
      <Menu open={snoozeOpen} onOpenChange={setSnoozeOpen}>
        <Tooltip disabled={snoozeOpen}>
          <TooltipTrigger
            render={
              <MenuTrigger
                render={
                  <Button
                    size={compact ? "icon-micro" : "icon-xs"}
                    variant="ghost-muted"
                    disabled={disabled}
                    aria-label="Snooze update"
                  />
                }
              />
            }
          >
            <Clock3Icon className={iconSize} />
          </TooltipTrigger>
          <TooltipPopup>Snooze update</TooltipPopup>
        </Tooltip>
        <MenuPopup align="end">
          {snoozed && (
            <MenuItem onClick={() => onChange({ snoozedUntil: undefined })}>Unsnooze</MenuItem>
          )}
          {snoozeOpen && <InboxSnoozeItems onChange={onChange} />}
        </MenuPopup>
      </Menu>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              // Icon-only, like its neighbours: the label lives in the tooltip.
              size={compact ? "icon-micro" : "icon-xs"}
              variant="ghost-muted"
              disabled={disabled}
              aria-label={archiveLabel}
              onClick={() => onChange({ settled: !state.settled, snoozedUntil: undefined })}
            />
          }
        >
          {state.settled ? (
            <Undo2Icon className={iconSize} />
          ) : (
            <ArchiveIcon className={iconSize} />
          )}
        </TooltipTrigger>
        <TooltipPopup>{archiveLabel}</TooltipPopup>
      </Tooltip>
    </div>
  );
}
